import { FC, ReactNode } from 'react'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import { Button, Text, Icon, Image } from '@/ui'
import { TypographyStyles } from '@/styles'

export interface OnboardingItemRowProps {
  header: string
  headerExtra?: ReactNode
  description: ReactNode
  imageUrl: string
  timer: {
    unit: string
    value: number
  }
  skippable: boolean
  additionalDetails?: ReactNode
  started: boolean
  completed: boolean
  skipped: boolean
  skippableIfStarted?: boolean
  onActionClick: (stepId?: number) => void
  onSkipClick?: () => void
  stepId?: number
}

const CloseIcon = ({
  onSkipClick,
  skippable,
  skipped,
  completed,
}: Pick<
  OnboardingItemRowProps,
  'onSkipClick' | 'skippable' | 'completed' | 'skipped'
>) => {
  if (!skippable || completed || skipped) return null
  return (
    <Button
      variant="discreet"
      iconLeft={regular('close')}
      size="sm"
      onClick={onSkipClick}
    />
  )
}

const ArrowRightIcon = () => (
  <Icon icon={regular('arrow-right')} className="ml-2" color="text-neutral-0" />
)

const IncompleteItemButtonContent = ({ started }: { started: boolean }) => (
  <>
    {started ? 'Continue' : 'Start'}
    <ArrowRightIcon />
  </>
)

export const OnboardingItemRow: FC<OnboardingItemRowProps> = (props) => {
  const {
    header,
    description,
    imageUrl,
    additionalDetails,
    timer,
    onActionClick,
    completed,
    skipped,
    started,
    stepId,
    headerExtra,
  } = props

  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const completedOrSkipped = completed || skipped
  const onActionClickWithStepId = () => onActionClick(stepId)

  return (
    <div
      className={`rounded-lg ${
        completedOrSkipped ? 'bg-neutral-25' : 'bg-priority-0'
      } ${isMobile ? 'p-4' : 'p-6'} w-full`}
    >
      <div className="flex flex-wrap md:flex-nowrap items-center w-full gap-4">
        <div className="flex items-center justify-center w-full md:w-1/6">
          <Image
            src={imageUrl}
            className={isMobile ? 'w-[45px]' : 'w-[80px]'}
            alt={header}
            objectFit="contain"
          />
          {isMobile && <CloseIcon {...props} />}
        </div>

        <div className="flex flex-col flex-grow gap-2 w-full md:w-3/5">
          <div className="flex items-start gap-4">
            <Text variant={TypographyStyles.H3}>{header}</Text>
            {headerExtra}
          </div>
          <Text variant={TypographyStyles.BODY_SM}>{description}</Text>
          <Text
            variant={TypographyStyles.BODY_XS}
            className="text-oak flex items-center gap-1"
          >
            <Icon icon={regular('timer')} />
            {timer.value} {timer.unit}
          </Text>
          {additionalDetails}
        </div>

        <div className="flex items-center justify-end gap-4 w-full md:w-1/5">
          {skipped ? null : completed ? (
            <Button
              variant="secondary"
              onClick={onActionClickWithStepId}
              disabled
            >
              <>
                <Icon icon={regular('check')} className="mr-2" />
                Done
              </>
            </Button>
          ) : (
            <Button onClick={onActionClickWithStepId}>
              <IncompleteItemButtonContent started={started} />
            </Button>
          )}
          {!isMobile && <CloseIcon {...props} />}
        </div>
      </div>
    </div>
  )
}
